/**
 * Flowto.AI 프로덕트 데이터
 * 기준: docs/homepage-renewal-plan-2026-08.md §3 (포트폴리오 정본)
 *
 * 화면 카피에 프로덕트 "개수"를 쓰지 않는다. 무엇을 만들고 있고
 * 무엇을 만들어 나갈지를 카테고리 테마(themes)로 묶어 말한다. (§3.0)
 *
 * @typedef {Object} Product
 * @property {string} slug
 * @property {string} name
 * @property {string} nameKo
 * @property {string} tagline
 * @property {string} description
 * @property {string} theme - themes의 key
 * @property {string} stripLabel - Theme Strip에 쓰는 짧은 설명
 * @property {'live'|'dev'|'suite'} group - Products 화면의 섹션 구분
 * @property {string} icon
 * @property {string} color
 * @property {'live'|'beta'|'wip'} status
 * @property {string} [storeUrl] - 스토어 링크. 카드/상세에 다운로드 버튼으로 노출
 * @property {string} [appIcon] - 스토어 앱 아이콘. 카드 썸네일 배지로 사용
 * @property {string[]} [screenshots] - 스토어 스크린샷. 카드 썸네일·상세 목업으로 사용
 * @property {Array<{width: number, height: number}>} [screenshotSizes] - screenshots와 같은 순서의 원본 크기
 *   (이미지가 없는 프로덕트는 썸네일 영역 자체를 렌더링하지 않는다 — 2026-08-26 사용자 결정)
 * @property {string} [externalUrl] - 값이 있으면 카드 클릭 시 이 URL을 새 탭으로 열고, 내부 /products/:slug 라우트도 이 URL로 리다이렉트
 * @property {Array<{title: string, desc: string}>} [features] - 4개 권장
 * @property {Array<{name: string, desc: string}>} [subApps] - 통합 앱(뚝딱)에 들어간 미니 도구
 */

/**
 * 카테고리 테마 — 서비스를 묶어 말하는 기준.
 * 프로덕트가 늘어도 테마는 유지된다. 신규 프로덕트는 기존 테마에 넣거나 테마를 추가한다.
 */
export const themes = [
  { key: 'travel', label: '여행', desc: '여행에서 만나고, 대처하는 서비스' },
  { key: 'habit', label: '건강·습관', desc: '캐릭터와 게이미피케이션으로 습관을 만드는 서비스' },
  { key: 'validation', label: '비즈니스 검증', desc: '아이디어를 실행 전에 검증하는 도구' },
  { key: 'daily', label: '생활 도구', desc: '일상의 작은 일을 처리하는 미니 도구' },
];

/** @type {Product[]} */
export const products = [
  {
    slug: 'meet-trip',
    name: 'Meettrip',
    nameKo: '밋트립',
    tagline: '같은 시기, 같은 도시를 여행하는 사람을 연결합니다',
    description:
      '혼자 떠난 일본 여행에서 맛집도 야경도 함께 볼 사람을 찾습니다. 일정과 관심사가 맞는 동행을 매칭하고, 검증된 장소에서 부담 없는 짧은 만남을 만듭니다.',
    theme: 'travel',
    stripLabel: '동행 매칭',
    group: 'live',
    icon: '🗾',
    color: '#3182F6',
    status: 'live',
    storeUrl: 'https://play.google.com/store/apps/details?id=com.flowto.meetrip',
    appIcon: '/images/products/meet-trip-icon.jpg',
    screenshots: [
      '/images/products/meet-trip-shot-1.jpg',
      '/images/products/meet-trip-shot-2.jpg',
    ],
    screenshotSizes: [
      { width: 1080, height: 2400 },
      { width: 1080, height: 1350 },
    ],
    features: [
      {
        title: '일정 기반 매칭',
        desc: '방문할 도시와 날짜만 등록하면 동선·여행 스타일·관심사가 겹치는 사람을 찾아줍니다.',
      },
      {
        title: '만남 카드',
        desc: '검증된 장소 중에서 만날 곳이 자동으로 제안됩니다. "어디서 볼까요" 대화가 필요 없습니다.',
      },
      {
        title: '약속 알림',
        desc: '약속 하루 전, 3시간 전, 30분 전에 알립니다. 답이 없으면 상대에게도 알려 약속이 지켜지게 합니다.',
      },
      {
        title: '안전 장치',
        desc: '만남 전 본인 확인, 신고·차단, 노쇼 시 매칭 제한. 낮 시간대 공개된 장소의 첫 만남을 권장합니다.',
      },
    ],
  },
  {
    slug: 'dalligo',
    name: 'Dalligo',
    nameKo: '달리고',
    tagline: 'AI 캐릭터 러닝메이트와 함께 걷고 달립니다',
    description:
      '운동 기록이 캐릭터와의 대화가 됩니다. 거리와 페이스를 아는 러닝메이트가 먼저 말을 걸고, 함께 달릴수록 캐릭터가 나를 기억하고 성장합니다.',
    theme: 'habit',
    stripLabel: 'AI 러닝메이트',
    group: 'live',
    icon: '🏃',
    color: '#00C896',
    status: 'live',
    storeUrl: 'https://play.google.com/store/apps/details?id=com.flowto.dallime',
    appIcon: '/images/products/dalligo-icon.jpg',
    screenshots: [
      '/images/products/dalligo-shot-1.jpg',
      '/images/products/dalligo-shot-2.jpg',
    ],
    screenshotSizes: [
      { width: 1080, height: 1920 },
      { width: 1254, height: 1254 },
    ],
    features: [
      {
        title: '기록이 대화가 됩니다',
        desc: '거리·시간·페이스가 그대로 대화에 반영됩니다. 오늘 얼마나 뛰었는지 아는 상대와 이야기합니다.',
      },
      {
        title: '기억하는 캐릭터',
        desc: '나눈 대화와 세운 목표를 기억했다가 다음에 꺼냅니다. 함께할수록 표정과 일러스트가 달라집니다.',
      },
      {
        title: '미션과 수집',
        desc: '매일 실천할 수 있는 가벼운 걷기·러닝 미션, 그리고 보상으로 모으는 일러스트 카드.',
      },
      {
        title: '스토리 러닝 코스',
        desc: '이야기를 따라 달리는 모드와 전용 BGM·보이스로 달리는 시간을 채웁니다.',
      },
    ],
  },
  {
    slug: 'marketfit-tester',
    name: 'MarketFit Tester',
    nameKo: '마켓핏 테스터',
    tagline: '아이디어를 실행하기 전에 검증합니다',
    description:
      '가게를 열기 전에, 서비스를 만들기 전에 시장이 원하는지부터 확인합니다. 소상공인이 자기 아이디어를 직접 테스트해볼 수 있는 웹 서비스입니다.',
    theme: 'validation',
    stripLabel: '아이디어 검증',
    group: 'dev',
    icon: '🎯',
    color: '#1B64DA',
    status: 'wip',
    features: [
      {
        title: '아이디어 정리',
        desc: '머릿속의 구상을 검증 가능한 형태의 가설로 정리합니다.',
      },
      {
        title: '반응 테스트',
        desc: '실제 고객이 될 사람들에게 물어보고, 답을 모읍니다.',
      },
      {
        title: '결과 해석',
        desc: '모인 응답에서 무엇을 바꾸고 무엇을 유지할지 정리해 보여줍니다.',
      },
      {
        title: '다음 단계 제안',
        desc: '검증 결과에 따라 지금 할 일을 알려줍니다.',
      },
    ],
  },
  {
    slug: 'jamkkan-eottae',
    name: 'Jamkkan',
    nameKo: '잠깐어때',
    tagline: '여행 중 생기는 돌발상황에 대처합니다',
    description:
      '비가 오고, 가려던 곳이 문을 닫고, 일정이 어긋납니다. 여행 중 예상치 못한 상황이 생겼을 때 지금 할 수 있는 대안을 찾아주는 모바일 서비스입니다.',
    theme: 'travel',
    stripLabel: '돌발상황 대응',
    group: 'dev',
    icon: '🧭',
    color: '#FFB800',
    status: 'wip',
    features: [
      {
        title: '상황 파악',
        desc: '날씨, 혼잡도, 영업 여부 등 지금 일정을 막고 있는 것이 무엇인지 확인합니다.',
      },
      {
        title: '대안 제시',
        desc: '현재 위치와 남은 시간에 맞는 대안을 제안합니다.',
      },
      {
        title: '일정 재구성',
        desc: '바뀐 계획에 맞춰 남은 동선을 다시 짭니다.',
      },
      {
        title: '함께 결정',
        desc: '동행이 있을 때 각자의 의견을 모아 다음 행선지를 정합니다.',
      },
    ],
  },
  {
    slug: 'tuktak-hub',
    name: 'Tuktak',
    nameKo: '뚝딱',
    tagline: '작게 만들어 본 도구들을 한 앱에 모았습니다',
    description:
      '일상의 작은 일을 처리하는 미니 도구 모음입니다. 각각 따로 만들어 보고 쓸 만한 것들을 하나의 앱으로 합쳤습니다.',
    theme: 'daily',
    stripLabel: '생활 미니 도구',
    group: 'suite',
    icon: '🛠️',
    color: '#7C3AED',
    status: 'live',
    subApps: [
      { name: '짤칵', desc: '상황에 맞는 짤과 밈을 만듭니다' },
      { name: '계약호구방지', desc: '계약서에서 불리한 조항을 찾아 짚어줍니다' },
      { name: '천기록', desc: '명리를 바탕으로 오늘의 운세를 기록합니다' },
      { name: '답풀', desc: '답하기 어려운 메시지를 상황에 맞는 톤으로 씁니다' },
      { name: '찍쿡', desc: '냉장고를 찍으면 가능한 요리를 추천합니다' },
    ],
  },
];

/**
 * 개편 전 개별 프로덕트 slug → 현재 slug.
 * 북마크·검색 유입이 404가 되지 않도록 /products/:slug 에서 리다이렉트한다.
 */
export const legacySlugRedirects = {
  'interview-people': 'marketfit-tester',
  'sign-guard': 'tuktak-hub',
  zzicook: 'tuktak-hub',
  jumgod: 'tuktak-hub',
  zzalkkak: 'tuktak-hub',
  dappul: 'tuktak-hub',
};

export const getProductBySlug = (slug) => products.find((p) => p.slug === slug);

export const getProductsByGroup = (group) =>
  products.filter((p) => p.group === group);

export const getThemeLabel = (themeKey) =>
  themes.find((t) => t.key === themeKey)?.label ?? '';

/** Theme Strip용 — 테마별로 소속 프로덕트를 묶는다. 소속 프로덕트가 없는 테마는 뺀다. */
export const getThemeGroups = () =>
  themes
    .map((theme) => ({
      ...theme,
      items: products.filter((p) => p.theme === theme.key),
    }))
    .filter((t) => t.items.length > 0);

export const getRelatedProducts = (slug, count = 3) => {
  const current = getProductBySlug(slug);
  if (!current) return products.slice(0, count);
  const sameTheme = products.filter(
    (p) => p.theme === current.theme && p.slug !== slug
  );
  const others = products.filter(
    (p) => p.theme !== current.theme && p.slug !== slug
  );
  return [...sameTheme, ...others].slice(0, count);
};
